"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { cn } from "@/lib/utils";

interface IResourceAccordionItemProps {
  title: string;
  /** Phosphor icon name (kebab-case) shown next to the title. */
  icon?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

/**
 * Expandable resource card (redesign): icon + bold title with a plus badge
 * that rotates 45° when open; the card border tints primary while expanded.
 */
export function ResourceAccordionItem({
  title,
  icon,
  children,
  defaultOpen = false,
}: IResourceAccordionItemProps): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[18px] border bg-card transition-colors duration-200",
        open ? "border-primary/45" : "border-border"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-3.5">
          {icon && (
            <PhosphorIcon
              name={icon}
              className="h-6 w-6 flex-none text-primary"
              aria-hidden="true"
            />
          )}
          <span className="font-sans text-lg font-bold leading-snug text-foreground">
            {title}
          </span>
        </span>
        <span
          className={cn(
            "grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-primary/10 text-primary transition-transform duration-200",
            open && "rotate-45"
          )}
        >
          <PhosphorIcon
            name="plus"
            weight="bold"
            className="h-[13px] w-[13px]"
            aria-hidden="true"
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="border-t border-border px-6 py-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
